import * as React from 'react';
import {
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Music2,
  Pause,
  Play,
  Video,
} from 'lucide-react';
import { ImageLightbox, type LightboxImage } from '@/components/ui/ImageLightbox';
import { cn } from '@/lib/utils/cn';
import { isSafeContentUrl } from '@/lib/utils/safe-url';
import { getAttachmentKind } from '../attachment-media';
import type { ChannelMessage } from '../queries';
import { useTranslation } from '@/lib/i18n/react';
import { formatNumber } from '@/lib/i18n/runtime';

interface TeamChatMediaProps {
  message: ChannelMessage;
  compact?: boolean;
  hasCaption?: boolean;
  inverse?: boolean;
  images?: LightboxImage[];
  onOpenImage?: (imageId: string) => void;
}

function formatBytes(value?: number): string | null {
  if (!value || value < 1) return null;
  if (!Number.isFinite(value)) return null;
  if (value < 1024) return `${formatNumber(value)} B`;
  if (value < 1024 * 1024) return `${formatNumber(Math.round(value / 1024))} KB`;
  return `${formatNumber(value / (1024 * 1024), {minimumFractionDigits:1,maximumFractionDigits:1})} MB`;
}

function formatTime(seconds: number): string {
  const whole = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
  return `${formatNumber(Math.floor(whole / 60), {useGrouping:false})}:${formatNumber(whole % 60, {useGrouping:false,minimumIntegerDigits:2})}`;
}

const VOICE_WAVE_HEIGHTS = Array.from(
  { length: 28 },
  (_, index) => 7 + ((index * 7) % 15),
);

function VoiceNotePlayer({ message, compact }: { message: ChannelMessage; compact: boolean }) {
  const { t, locale } = useTranslation('teamchat');
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const url = message.file_url ?? '';
  const waveHeights = compact ? VOICE_WAVE_HEIGHTS.slice(0, 18) : VOICE_WAVE_HEIGHTS;
  const progressPercent = duration > 0
    ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
    : 0;

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  };

  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);
    if (audioRef.current) audioRef.current.currentTime = next;
    setCurrentTime(next);
  };

  return (
    <div className={cn('tc-voice-note', compact && 'is-compact')}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
        onDurationChange={(event) => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        type="button"
        className="tc-voice-play"
        onClick={togglePlayback}
        aria-label={t(playing ? 'media.voicePause' : 'media.voicePlay')}
      >
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="tc-voice-body">
        <div className="tc-voice-heading">
          <span><Music2 size={13} /> {t('media.voiceNote')}</span>
          <span><bdi dir="ltr">{formatTime(currentTime)} / {formatTime(duration)}</bdi></span>
        </div>
        <div className="tc-voice-track">
          <div className="tc-voice-wave is-base" aria-hidden="true">
            {waveHeights.map((height, index) => (
              <i key={index} style={{ height: `${height}px` }} />
            ))}
          </div>
          <div
            className="tc-voice-wave is-progress"
            aria-hidden="true"
            style={{ clipPath: locale === 'ar' ? `inset(0 0 0 ${100 - progressPercent}%)` : `inset(0 ${100 - progressPercent}% 0 0)` }}
          >
            {waveHeights.map((height, index) => (
              <i key={index} style={{ height: `${height}px` }} />
            ))}
          </div>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || 0)}
            onChange={seek}
            aria-label={t('media.voiceSeek')}
            aria-valuetext={t('media.voicePosition', {current:formatTime(currentTime),duration:formatTime(duration)})}
          />
        </div>
        {!compact && message.file_name && <bdi dir="auto" className="tc-media-filename">{message.file_name}</bdi>}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="tc-media-open"
        aria-label={t('media.openVoice')}
        title={t('media.openFile')}
      >
        <ExternalLink size={14} />
      </a>
    </div>
  );
}

/**
 * One attachment renderer for every internal-chat surface. Forwarded Inbox
 * media is classified from its original type, so it keeps the same inline
 * preview/player even when the team-chat API stores the row as `file`.
 */
export function TeamChatMedia({
  message,
  compact = false,
  hasCaption = false,
  inverse = false,
  images,
  onOpenImage,
}: TeamChatMediaProps) {
  const { t } = useTranslation('teamchat');
  const [localLightboxOpen, setLocalLightboxOpen] = React.useState(false);
  const url = message.file_url;
  if (!url) return null;

  const safe = isSafeContentUrl(url);
  const kind = getAttachmentKind(message);
  const filename = message.file_name || t(kind === 'image' ? 'media.image' : 'media.attachment');
  const size = formatBytes(message.file_size);
  const ownImage: LightboxImage = {
    id: message.id ?? url,
    url,
    alt: filename,
    caption: filename,
  };
  const lightboxImages = images?.length ? images : [ownImage];
  const imageId = message.id ?? url;

  if (!safe) {
    return (
      <span
        className={cn('tc-media-file is-blocked', compact && 'is-compact', hasCaption && 'has-caption')}
        aria-disabled="true"
        title={t('media.blockedTitle')}
      >
        <span className="tc-media-file-icon"><FileText size={18} /></span>
        <span><strong><bdi dir="auto">{filename}</bdi></strong><small>{t('media.blockedReason')}</small></span>
      </span>
    );
  }

  if (kind === 'image') {
    const openImage = () => {
      if (onOpenImage) onOpenImage(imageId);
      else setLocalLightboxOpen(true);
    };
    return (
      <>
        <button
          type="button"
          className={cn(
            'tc-media-card tc-media-image',
            compact && 'is-compact',
            hasCaption && 'has-caption',
          )}
          onClick={openImage}
          title={t('media.viewImage')}
        >
          <img src={url} alt={filename} loading="lazy" />
          <span className="tc-media-image-overlay">
            <span><ImageIcon size={15} /> {t('media.viewGallery')}</span>
            {lightboxImages.length > 1 && <small>{t('media.photos', {count:lightboxImages.length})}</small>}
          </span>
        </button>
        {!onOpenImage && (
          <ImageLightbox
            images={lightboxImages}
            initialIndex={Math.max(0, lightboxImages.findIndex((image) => image.id === imageId))}
            open={localLightboxOpen}
            onClose={() => setLocalLightboxOpen(false)}
          />
        )}
      </>
    );
  }

  if (kind === 'video') {
    return (
      <div className={cn('tc-media-card tc-media-video', compact && 'is-compact', hasCaption && 'has-caption')}>
        <div className="tc-media-video-head">
          <span><Video size={14} /> {t('media.video')}</span>
          {size && <small><bdi>{size}</bdi></small>}
        </div>
        <video controls playsInline preload="metadata" src={url} aria-label={filename} />
        <a href={url} target="_blank" rel="noreferrer" className="tc-media-video-foot">
          <span title={filename}><bdi dir="auto">{filename}</bdi></span>
          <ExternalLink size={14} />
        </a>
      </div>
    );
  }

  if (kind === 'audio') {
    return (
      <div className={cn('tc-media-card tc-media-audio', hasCaption && 'has-caption', inverse && 'is-inverse')}>
        <VoiceNotePlayer message={message} compact={compact} />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={cn('tc-media-file', compact && 'is-compact', hasCaption && 'has-caption')}
    >
      <span className="tc-media-file-icon"><FileText size={18} /></span>
      <span>
        <strong title={filename}><bdi dir="auto">{filename}</bdi></strong>
        <small><bdi>{[message.file_mime_type, size].filter(Boolean).join(' · ') || t('media.attachment')}</bdi></small>
      </span>
      <ExternalLink size={15} />
    </a>
  );
}
